import { useEffect, useMemo, useState } from 'react';
import { ANSWER_WINDOW_MS } from '@sb2/shared';
import type { QuizQuestion } from '../../api/client';

const LABELS = ['A', 'B', 'C', 'D'] as const;

interface Props {
  question: QuizQuestion;
  deadlineAt: number | null;
  /** Full timer window for this question (ms). Defaults to 5s answer window. */
  windowMs?: number | null;
  /** When false, answers stay laid out but veiled until reading ends. */
  showChoices?: boolean;
  disabled?: boolean;
  highlightCorrect?: number | null;
  selected?: number | null;
  onAnswer: (choiceIndex: number) => void;
}

export function QuizBoard({
  question,
  deadlineAt,
  windowMs,
  showChoices = true,
  disabled,
  highlightCorrect,
  selected,
  onAnswer,
}: Props) {
  const [now, setNow] = useState(Date.now());
  const span = Math.max(1, windowMs ?? ANSWER_WINDOW_MS);
  const reading = !showChoices;

  useEffect(() => {
    if (reading) return;
    const id = window.setInterval(() => setNow(Date.now()), 50);
    return () => window.clearInterval(id);
  }, [question.id, reading]);

  // Drop sticky focus/hover from the previous answer when a new question mounts.
  useEffect(() => {
    const el = document.activeElement;
    if (el instanceof HTMLElement) el.blur();
  }, [question.id]);

  const remainingMs = useMemo(() => {
    if (!deadlineAt) return span;
    return Math.max(0, deadlineAt - now);
  }, [deadlineAt, now, span]);

  const remaining = Math.max(0, Math.min(1, remainingMs / span));
  const seconds = Math.ceil(remainingMs / 1000);

  return (
    <div className={`quiz-board${reading ? ' reading' : ''}`}>
      <div className="quiz-meta">
        <span className={`diff-pill diff-${question.difficulty}`}>{question.difficulty}</span>
        <span className="cat-pill">{question.category}</span>
        {!reading ? (
          <div className="timer-bar" aria-label={`${seconds} seconds left`}>
            <div className="timer-fill" style={{ transform: `scaleX(${remaining})` }} />
            <span className="timer-label">{seconds}s</span>
          </div>
        ) : (
          <div className="timer-bar timer-bar-spacer" aria-hidden="true" />
        )}
      </div>
      <h2 className="quiz-prompt">{question.prompt}</h2>
      <div className="choices">
        {question.choices.map((c) => {
          const isSel = selected === c.index;
          const isCorrect = highlightCorrect === c.index;
          const isWrong = highlightCorrect != null && isSel && !isCorrect;
          return (
            <button
              key={`${question.id}-${c.index}`}
              type="button"
              className={[
                'choice',
                reading ? 'veiled' : '',
                isSel ? 'selected' : '',
                isCorrect ? 'correct' : '',
                isWrong ? 'wrong' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={disabled || reading}
              tabIndex={reading ? -1 : 0}
              onClick={(e) => {
                e.currentTarget.blur();
                onAnswer(c.index);
              }}
            >
              <span className="choice-key">{LABELS[c.index]}</span>
              <span className="choice-text">{c.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
